import { AuthCategory, AuthMenuCode } from "@/declare/enums/auth";
import { RsUserAuth, RsUserFileAuth } from "@/declare/interface/permission";
import { useCallback, useEffect, useState } from "react";
import localStore from "./local-store";
import { WANGLEI_USER_ID } from "./constant";

export enum JudgeAuthResult {
    NO_AUTH = 1,
    HAS_AUTH,
    WAITING, // 尚未获取过权限
}

export interface UserAuthStatus {
    userAuths: RsUserAuth[];
    onUserAuthsChange: (userAuths: RsUserAuth[]) => void;
    userFileAuths: RsUserFileAuth[];
    onUserFileAuthsChange: (userAuths: RsUserFileAuth[]) => void;

    judgeHasAuth: (needs: { [key: number]: AuthCategory[] }, projectid?: number, isOr?: boolean) => JudgeAuthResult;
    judgeHasFileAuth: (needs: { [key: number]: AuthCategory[] }, projectid?: number, projectFileId?: number, isOr?: boolean) => JudgeAuthResult; // 判断是否有文件权限
    hasGetAuth: boolean;
    hasGetFileAuth: boolean;
    judgeHasAuthByKeyCode: (keyCode: AuthMenuCode, codes: AuthCategory[]) => boolean
}

/**
 * 系统存储
 * @returns 
 */
export function useUserAuthService() {
    const [userAuths, setUserAuths] = useState<RsUserAuth[]>([]);
    const [userFileAuths, setUserFileAuths] = useState<RsUserFileAuth[]>([]);
    const [hasGetAuth, setHasGetAuth] = useState(false);    // 是否至少获取过一次权限
    const [hasGetFileAuth, setHasGetFileAuth] = useState(false);    // 是否至少获取过一次文件权限


    const onUserAuthsChange = useCallback((value: RsUserAuth[]) => {
        setUserAuths(value);
        setHasGetAuth(true);
    }, [])

    const onUserFileAuthsChange = useCallback((value: RsUserFileAuth[]) => {
        setUserFileAuths(value);
        setHasGetFileAuth(true);
    }, [])

    const judgeHasAuth = useCallback((needs: { [key: number]: AuthCategory[] }, projectId: number = null, isOr?: boolean) => {
        if (localStore?.userId == WANGLEI_USER_ID) {
            return JudgeAuthResult.HAS_AUTH
        }
        if (!needs) return JudgeAuthResult.HAS_AUTH;
        const needCodes = Object.keys(needs);  // 需要的功能
        if (needCodes.length == 0) return JudgeAuthResult.HAS_AUTH;

        if (userAuths == null || !userAuths || userAuths.length == 0) {
            if (!hasGetAuth) return JudgeAuthResult.WAITING;
            return JudgeAuthResult.NO_AUTH;
        }
        // 这是当前项目下的权限，库权限的话这里是null
        const filterUserAuths = userAuths.filter(item => item.projectId == projectId);
        if (filterUserAuths.length == 0) return JudgeAuthResult.NO_AUTH;
        // userAuths 根据code分组
        const groupedAuth = filterUserAuths.reduce<{ [key: number]: RsUserAuth[] }>((acc, item) => {
            // 根据 code 分组
            if (!acc[item.auth.code]) {
                acc[item.auth.code] = [];
            }
            acc[item.auth.code].push(item);
            return acc;
        }, {})


        for (const code of needCodes) {
            const group = groupedAuth[code];
            if (!group) {
                if (isOr) {
                    continue;
                }
                return JudgeAuthResult.NO_AUTH;
            }
            const categroys = needs[code];
            if (categroys == null || categroys.length == 0) continue;
            let hasAuth = false; // 是否有权限
            for (const categroy of categroys) {
                if (categroy === AuthCategory.READ) {
                    hasAuth = group.some(item => item.auth.category >= AuthCategory.READ);
                } else {
                    hasAuth = group.some(item => item?.auth?.category === categroy);
                }
                // 是否是 “与” 权限组
                if (isOr) {
                    if (hasAuth) {
                        return JudgeAuthResult.HAS_AUTH;
                    }
                } else {
                    if (!hasAuth) {
                        return JudgeAuthResult.NO_AUTH;
                    }
                }

            }
        }
        if (isOr) {
            return JudgeAuthResult.NO_AUTH;
        } else {
            return JudgeAuthResult.HAS_AUTH;
        }
    }, [userAuths, hasGetAuth])

    const judgeHasFileAuth = useCallback((needs: { [key: number]: AuthCategory[] }, projectId: number = null, projectFileId: number = null, isOr?: boolean) => {
        if (localStore?.userId == WANGLEI_USER_ID) {
            return JudgeAuthResult.HAS_AUTH
        }
        if (!needs) return JudgeAuthResult.HAS_AUTH;
        const needCodes = Object.keys(needs);  // 需要的功能
        if (needCodes.length == 0) return JudgeAuthResult.HAS_AUTH;

        if (userFileAuths == null || !userFileAuths || userFileAuths.length == 0) {
            if (!hasGetFileAuth) return JudgeAuthResult.WAITING;
            return JudgeAuthResult.NO_AUTH;
        }
        // 这是当前项目下的某文件目录下的权限
        const filterUserAuths = userFileAuths.filter(item => item.projectId == projectId && item.projectFileId == projectFileId);
        if (filterUserAuths.length == 0) return JudgeAuthResult.NO_AUTH;
        // userAuths 根据code分组
        const groupedAuth = filterUserAuths.reduce<{ [key: number]: RsUserFileAuth[] }>((acc, item) => {
            // 根据 code 分组
            if (!acc[item.auth.code]) {
                acc[item.auth.code] = [];
            }
            acc[item.auth.code].push(item);
            return acc;
        }, {})


        for (const code of needCodes) {
            const group = groupedAuth[code];
            if (!group) {
                if (isOr) {
                    continue;
                }
                return JudgeAuthResult.NO_AUTH;
            }
            const categroys = needs[code];
            if (categroys == null || categroys.length == 0) continue;
            let hasAuth = false; // 是否有权限
            for (const categroy of categroys) {
                if (categroy === AuthCategory.READ) {
                    hasAuth = group.some(item => item.auth.category >= AuthCategory.READ);
                } else {
                    hasAuth = group.some(item => item?.auth?.category === categroy);
                }
                // 是否是 “与” 权限组
                if (isOr) {
                    if (hasAuth) {
                        return JudgeAuthResult.HAS_AUTH;
                    }
                } else {
                    if (!hasAuth) {
                        return JudgeAuthResult.NO_AUTH;
                    }
                }

            }
        }
        if (isOr) {
            return JudgeAuthResult.NO_AUTH;
        } else {
            return JudgeAuthResult.HAS_AUTH;
        }
    }, [userFileAuths, hasGetFileAuth])

    const judgeHasAuthByKeyCode = (keyCode: AuthMenuCode, codes: AuthCategory[]) => {
        const res = judgeHasAuth({ [keyCode]: codes }, null);
        if (res === JudgeAuthResult.NO_AUTH) {
            return false;
        }
        return true;
    }

    return {
        userAuths,
        userFileAuths,
        onUserAuthsChange,
        onUserFileAuthsChange,
        judgeHasAuth,
        judgeHasFileAuth,
        hasGetFileAuth,
        judgeHasAuthByKeyCode
    } as UserAuthStatus
}
